import { ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Stack,
  Typography,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Alert,
} from "@mui/material";
import { useTTS } from "shared/hooks/useTTS";
import { downloadAsJSON, downloadAsWAV } from "shared/utils/tts";
import { useDownload } from "shared/hooks/useDownload";
import { getWrappedInput } from "shared/utils/tts";
import {
  selectedValuesAtom,
  userInputAtom,
  sampleRateOptionsAtom,
} from "atoms";
import { useAtom, useAtomValue } from "jotai";
import { voicesDescriptionAtom } from "atoms";
import { CustomDialog } from "@web-applications/allinga-web-components";
import SampleRateSelector from "./SampleRateSelector";

export interface TTSDownloadNotificationProps {
  open: boolean;
  closeNotification: () => void;
}

function TTSDownloadNotification({
  open,
  closeNotification,
}: TTSDownloadNotificationProps): ReactElement {
  const { t } = useTranslation();
  const [shouldDownloadAudio, setShouldDownloadAudio] =
    useState<boolean>(false);
  const [shouldDownloadJSON, setShouldDownloadJSON] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [ttsError, setTtsError] = useState<boolean>(false);
  const [audioCheckComplete, setAudioCheckComplete] = useState<boolean>(false);
  const { getTTSRequestUrl } = useTTS();
  const { notifyDownloadResult } = useDownload();
  const userInput = useAtomValue(userInputAtom);
  const voicesDescription = useAtomValue(voicesDescriptionAtom);
  const sampleRateOptions = useAtomValue(sampleRateOptionsAtom);

  const [selectedValues, setSelectedValues] = useAtom(selectedValuesAtom);
  const playPauseSampleRate = selectedValues.selectedSampleRate;
  const [downloadSampleRate, setDownloadSampleRate] = useState<string>(
    sampleRateOptions[0]?.value
  );

  const selectedVoice = voicesDescription.find(
    (voice) => voice.voiceName === selectedValues.selectedVoice
  );

  const voiceName = selectedVoice?.voiceName || "defaultVoiceName";
  const nativeLanguage = selectedVoice?.nativeLanguage || "defaultLanguage";

  const handleClose = () => {
    setSelectedValues({
      ...selectedValues,
      selectedSampleRate: playPauseSampleRate,
    });
    closeNotification();
  };

  // Handle sample rate changes from the selector
  const handleSampleRateChange = (newSampleRate: string) => {
    setDownloadSampleRate(newSampleRate);
  };

  const handleDownloadClick = async () => {
    if (!shouldDownloadAudio && !shouldDownloadJSON) return;

    setIsProcessing(true);

    try {
      if (shouldDownloadAudio) {
        const { url } = await getTTSRequestUrl(true, downloadSampleRate);
        downloadAsWAV(
          url,
          voiceName,
          nativeLanguage,
          notifyDownloadResult,
          downloadSampleRate
        );
      }
      const ssmlContent = getWrappedInput(userInput);
      const dataObj = {
        environment: selectedValues.selectedEnv,
        flavor: selectedValues.selectedDeployment,
        text: ssmlContent,
        voice: voiceName,
        language: nativeLanguage,
        samplerate: parseInt(downloadSampleRate, 10),
      };

      const jsonformate = JSON.stringify(dataObj, null, 2);

      if (shouldDownloadJSON) {
        downloadAsJSON(
          jsonformate,
          voiceName,
          nativeLanguage,
          notifyDownloadResult,
          downloadSampleRate
        );
      }

      handleClose();
    } catch (err) {
      setTtsError(true);
      setIsProcessing(false);
      // eslint-disable-next-line no-console
      console.error("TTS download failed:", err);
    } finally {
      setAudioCheckComplete(true);
    }
  };

  // Custom label component that handles click events on the sample rate selector
  const audioCheckboxLabel = (
    <div style={{ display: "flex", alignItems: "center" }}>
      <span style={{ paddingRight: "16px" }}>{t("tts.download.option1")}</span>
      <SampleRateSelector
        showBorder={true}
        paddingRight="8px"
        onSampleRateChange={handleSampleRateChange}
        onClick={(e) => {
          // Stop propagation to prevent checkbox toggle when clicking on SampleRateSelector
          e.stopPropagation();
        }}
      />
    </div>
  );

  return (
    <CustomDialog
      open={open}
      showDivider={false}
      showCloseIcon
      header={t("tts.download.header")}
      handleOnClose={handleClose}
      primaryButtonText={t("buttons.download")}
      onPrimaryButtonClick={handleDownloadClick}
      isPrimaryButtonDisabled={
        (!shouldDownloadAudio && !shouldDownloadJSON) || isProcessing
      }
      tertiaryButtonText={t("buttons.cancel")}
      onTertiaryButtonClick={handleClose}
      maxWidth="sm"
      height="auto"
    >
      {isProcessing ? (
        <Stack direction="row" p={1} gap={1}>
          <CircularProgress size={24} color="primary" />
          <Typography variant="body2">{t("tts.download.checking")}</Typography>
        </Stack>
      ) : (
        <Stack gap={1}>
          <Alert severity="info" sx={{ mb: 1 }}>
            {t("notifications.ttsDownloadInfo")}
          </Alert>
          <Typography variant="body1">{t("tts.download.text")}</Typography>
          <Stack p="9px" spacing="8px">
            <FormControlLabel
              control={
                <Checkbox
                  checked={shouldDownloadAudio}
                  onChange={(e) => setShouldDownloadAudio(e.target.checked)}
                  disabled={ttsError}
                />
              }
              label={audioCheckboxLabel}
            />
            {ttsError && audioCheckComplete && (
              <Typography variant="body2" pl={4} color="error.dark">
                {t("notifications.ttsDownloadAudioFailed")}
              </Typography>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={shouldDownloadJSON}
                  onChange={(e) => setShouldDownloadJSON(e.target.checked)}
                  sx={{ borderRadius: 0 }}
                />
              }
              label={t("tts.download.option2")}
            />
          </Stack>
        </Stack>
      )}
    </CustomDialog>
  );
}

export default TTSDownloadNotification;
